import * as React from 'preact/compat'
import SteamGrid, { type SteamGridProps } from '../SteamGrid'
import ImageCrop, { type ImageCropProps } from '../ImageCrop'
import LogoUpload_react from '../LogoUpload_react'
import { sgAlert, sgConfirm } from '../../plugins/SgAlert'
import { getCurrentScheme } from '../../plugins/color-scheme'
import { showModal } from '../../common/helpers'
import commonCss from '../../../css/common.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'

interface PropTypes {
  src: string,
  type: 'square' | 'large',
  updateImage: (src: string, files: FileList) => void,
  steamId?: string,
  gameName?: string,
  oldLogo?: string,
  onClose?: () => void
}

interface StateTypes {
  src: string,
  files?: FileList,
}

const setBroken = (fallbackSrc: string) => {
  const onError = (event: React.TargetedEvent<HTMLImageElement>) => {
    const theme: 'dark'|'light' = getCurrentScheme()
    event.currentTarget.src = `/img/backdrop-${fallbackSrc}-${theme}.png`;
    event.currentTarget.removeEventListener('error', onError)
  }
  return onError
}

export default class GameCoverEditor extends React.Component<PropTypes, StateTypes> {

  state: StateTypes = {
    src: this.props.src,
  }

  componentDidUpdate(previousProps: Readonly<PropTypes>) {
    if (this.props.src !== previousProps.src) {
      this.setState({src: this.props.src})
    }
  }

  styles = {
    square: {
      width: '250px',
      height: '250px',
      borderRadius: '8px',
      marginBottom: '24px'
    },
    large: {
      width: 'auto',
      height: '250px',
      borderRadius: '8px',
      marginBottom: '24px'
    }
  }

  steamGridTypes = {
    square: 'grid',
    large: 'hero'
  }

  setSrc = (src: string, shouldCrop: boolean = false) => {
    if (shouldCrop) {
      const { type } = this.props
      showModal(ImageCrop, {
        image: `/ajax/steam-grid/steal-image?url=${encodeURIComponent(src)}`,
        size: this.cropSizes[type] as ImageCropProps['size'],
        onCropComplete: this.onCropComplete
      })
      return
    }

    this.setState({
      src,
      files: undefined
    })
  }

  steam = async () => {
    if (this.props.type === 'square') {
      const sources = [
        `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${this.props.steamId}/library_600x900_2x.jpg`,
        `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${this.props.steamId}/library_600x900.jpg`,
        `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${this.props.steamId}/hero_capsule.jpg`,
      ]
      let url = null
      for (const src of sources) {
        const res = await fetch(`/ajax/steam-grid/steal-image?url=${encodeURIComponent(src)}`)
        if (res.ok) {
          url = src
          break
        }
      }
      if (url === null) {
        sgAlert('Не удалось найти изображение обложки на сервере Steam')
        return
      }

      this.setSrc(url, true)
    } else {
      this.setSrc(`https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${this.props.steamId}/library_hero.jpg`)
    }
  }

  steamGridById = () => {
    const { steamId, type } = this.props

    showModal(SteamGrid, {
      steamId: steamId,
      type: this.steamGridTypes[type] as SteamGridProps['type'],
      onSelect: this.setSrc,
    })
  }

  steamGridByName = () => {
    const { gameName, type } = this.props

    showModal(SteamGrid, {
      name: gameName,
      type: this.steamGridTypes[type] as SteamGridProps['type'],
      onSelect: this.setSrc,
    })
  }

  cropSizes = {
    square: [1024, 1024],
    large: [1920, 620]
  }

  onCropComplete = async (imgData: string) => {
    const imgBlob = await fetch(imgData).then(res => res.blob())
    const file = new File([imgBlob], 'game-logo', { type: 'image/png' })
    const container = new DataTransfer()
    container.items.add(file)
    this.setState({
      src: imgData,
      files: container.files
    })
  }

  cropOldLogo = () => {
    const { oldLogo, type } = this.props

    showModal(ImageCrop, {
      image: oldLogo,
      size: this.cropSizes[type] as ImageCropProps['size'],
      onCropComplete: this.onCropComplete
    })
  }

  uploadDone = (data: {image: string, files: FileList}) => {
    this.setState({
      src: data.image,
      files: data.files
    })
  }

  saveImage = () => {
    this.props.updateImage(this.state.src, this.state.files)
    if (this.props.onClose) {
      this.props.onClose()
    }
  }

  deleteImage = async () => {
    if (await sgConfirm('Действительно удалить изображение?')) {
      this.setSrc('')
    }
  }

  render() {
    const { type, steamId, gameName, oldLogo } = this.props
    const { src } = this.state

    const googleUrl = new URL('https://www.google.com/search')
    googleUrl.searchParams.set('tbm', 'isch')
    googleUrl.searchParams.set('as_q', `${gameName} game`)
    googleUrl.searchParams.set('as_st', 'y')
    googleUrl.searchParams.set('safe', 'images')
    googleUrl.searchParams.set('tbs', type === 'square' ? 'iar:s': 'iar:w')

    return <div>
      <h1 className={commonCss['h3']} style='margin-bottom: 24px'>Редактирование обложки</h1>
      <img style={this.styles[type]} src={src} alt='' onError={setBroken(type)} />
      {(steamId || gameName || (oldLogo && type === 'square')) &&
        <div style='display: flex; column-gap: 16px; margin-bottom: 16px'>
          {steamId && <button
            type='button'
            className={buttonsCss['default']}
            onClick={this.steam}
          >
            Из Steam
          </button>}
          {steamId && <button
              type='button'
              className={buttonsCss['default']}
              onClick={this.steamGridById}
            >
              Из SteamGrid
          </button>}
          {!steamId && gameName && <button
              type='button'
              className={buttonsCss['default']}
              onClick={this.steamGridByName}
            >
              Из SteamGrid
          </button>}
          {gameName && <a
            href={googleUrl.toString()}
            target='_blank'
            className={buttonsCss['default']}
            >
              Искать в Google
          </a>}
          {oldLogo && type === 'square' && <button
            type='button'
            className={buttonsCss['default']}
            onClick={this.cropOldLogo}
            >
              Кадрировать существующую обложку
          </button>}
        </div>
      }
      <div style='display: flex; column-gap: 16px;'>
        <LogoUpload_react
          value={src}
          name='doesnt-matter'
          preview={false}
          crop={this.cropSizes[type].join('×')}
          onDone={this.uploadDone}
          note=''
          placeholder='Загрузить обложку'
        />
        {this.state.src &&
          <button type='button' className={buttonsCss['outline']} onClick={this.deleteImage}>Удалить постер</button>
        }
      </div>
      <button type='button' style='margin-top: 16px' className={buttonsCss['primary']} onClick={this.saveImage}>Сохранить</button>
    </div>
  }
}
